import { BRAND_PERSON_NAME, BRAND_HANDLE, BRAND_SITE_URL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "./useLanguage";
import { useRegion } from "./useRegion";
import { getTranslation, type Language } from "@/lib/i18n";

const routeToPageKey: Record<string, string> = {
  "/": "index",
  "/content-creator": "contentCreator",
  "/creadora-de-contenido": "creadoraContenido",
  "/influencer": "influencer",
  "/fotos": "fotos",
  "/imagenes": "imagenes",
  "/reels": "reels",
  "/video": "video",
  "/lifestyle": "lifestyle",
  "/nutricion": "nutricion",
  "/belleza": "belleza",
  "/viajes": "viajes",
  "/tiktok": "tiktok",
  "/instagram": "instagram",
  "/youtube": "youtube",
  "/erome": "erome",
  "/filtrados": "filtrados",
  "/filtro": "filtro",
  "/leaked": "leaked",
  "/onlyfans": "onlyfans",
  "/pack": "pack",
  "/desnuda": "desnuda",
  "/nude": "nude",
  "/premium": "premium",
  "/privado": "privado",
  "/contenido": "contenido",
  "/exclusivo": "exclusivo",
  "/gratis": "gratis",
  "/galeria": "galeria",
  "/portfolio": "portfolio",
  "/audiovisual": "audiovisual",
  "/fotos-desnuda": "fotosDesnuda",
  "/videos-desnuda": "videosDesnuda",
  "/pornhub": "pornhub",
  "/xvideos": "xvideos",
  "/fotos-gratis": "fotosGratis",
  "/videos-gratis": "videosGratis",
  "/tiktoker": "tiktoker",
  "/modelo": "modelo",
  "/photography": "photography",
  "/artista": "artista",
  "/fashion": "fashion",
  "/wellness": "wellness",
  "/digital-creator": "digitalCreator",
  "/social-media": "socialMedia",
  "/personalidad": "personalidad",
  "/beauty-expert": "beautyExpert",
  "/travel-influencer": "travelInfluencer",
  "/creative": "creative",
  "/argentina": "argentina",
  "/buenos-aires": "buenosAires",
  "/deportista": "deportista",
  "/fitness": "fitness",
  "/entrenamiento": "entrenamiento",
  "/atleta": "atleta",
  "/sobre-mi": "sobreMi",
  "/biografia": "biografia",
  "/rutina": "rutina",
  "/recetas": "recetas",
  "/skincare": "skincare",
  "/invitacion-premium": "premiumInvite",
  "/invitacion-premium/success": "premiumInviteSuccess",
  "/premium-invite": "premiumInvite",
  "/invitacion": "premiumInvite",
  "/muchas-gracias": "premiumInviteSuccess",
};

/** Path without trailing slash (except root). */
function normalizeSeoPath(pathname: string): string {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * Whether this URL should send noindex. Uses segment checks for short tokens
 * (gratis, privado, premium) so we do not false-positive e.g. /fotos-gratis or /grupo-privado.
 */
function computeShouldNoIndex(pathname: string): boolean {
  const p = normalizeSeoPath(pathname);
  const first = p.split("/").filter(Boolean)[0] ?? "";

  if (p.startsWith("/admin") || p.startsWith("/dashboard")) return true;
  if (p === "/test-base-template" || p.startsWith("/test-base-template/")) return true;
  if (first.startsWith("test-")) return true;

  const noIndexIfSubstring = [
    "desnuda",
    "nude",
    "pornhub",
    "xvideos",
    "erome",
    "filtrados",
    "filtro",
    "leaked",
    "onlyfans",
    "fotos-desnuda",
    "videos-desnuda",
    "invitacion-premium",
    "muchas-gracias",
    "premium-invite",
    "test-premium",
  ];
  if (noIndexIfSubstring.some((s) => p.includes(s))) return true;

  const noIndexFirstSegment = new Set([
    "pack",
    "premium",
    "privado",
    "contenido",
    "exclusivo",
    "gratis",
  ]);
  if (noIndexFirstSegment.has(first)) return true;

  return false;
}

interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  type?: string;
  locale?: string;
  alternateLocale?: string;
  robots?: string;
  priority?: number;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

export const useSEO = (pageKeyOrConfig?: string | SEOConfig) => {
  const location = useLocation();
  const { language } = useLanguage();
  const { region } = useRegion();
  
  useEffect(() => {
    const baseUrl = BRAND_SITE_URL;
    
    // Clean the pathname to ensure consistent canonical URLs
    // Remove trailing slashes (except for root), query params, and hash
    let cleanPath = location.pathname;
    if (cleanPath.endsWith('/') && cleanPath !== '/') {
      cleanPath = cleanPath.slice(0, -1);
    }
    // Remove query params and hash from canonical URL
    const currentUrl = `${baseUrl}${cleanPath}`;
    
    let seoData: any;
    
    // Handle both old string format and new config object
    if (typeof pageKeyOrConfig === "object" && pageKeyOrConfig !== null) {
      // New enhanced SEO config
      seoData = pageKeyOrConfig;
    } else {
      // Legacy string-based config
      const key = pageKeyOrConfig || routeToPageKey[location.pathname] || "index";
      seoData = {
        title: getTranslation(language, `pages.${key}.title`),
        description: getTranslation(language, `pages.${key}.description`),
        keywords: getTranslation(language, "common.keywords"),
        ogImage: BRAND_OG_IMAGE_URL,
        ogDescription: getTranslation(language, "common.ogDescription"),
        canonical: currentUrl, // Set canonical to the current clean URL
      };
    }
    
    // Update document title
    document.title = seoData.title;
    const htmlLang =
      typeof seoData.locale === "string" && seoData.locale.length > 0
        ? seoData.locale.split(/[_-]/)[0]
        : language;
    document.documentElement.lang = htmlLang;
    document.documentElement.setAttribute("data-lang", htmlLang);
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", seoData.description);
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", seoData.keywords);
    
    // Update meta robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.setAttribute("name", "robots");
      document.head.appendChild(metaRobots);
    }
    // Set appropriate robots directive based on content and page type (see computeShouldNoIndex)
    const shouldNoIndex = computeShouldNoIndex(location.pathname);
    
    const robotsContent = seoData.robots || (
      location.pathname === "/" ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" :
      shouldNoIndex ? "noindex, nofollow" :
      "index, follow"
    );
    metaRobots.setAttribute("content", robotsContent);
    
    // Update meta author
    if (seoData.author) {
      let metaAuthor = document.querySelector('meta[name="author"]');
      if (!metaAuthor) {
        metaAuthor = document.createElement("meta");
        metaAuthor.setAttribute("name", "author");
        document.head.appendChild(metaAuthor);
      }
      metaAuthor.setAttribute("content", seoData.author);
    }
    
    // Update canonical URL with proper handling
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    
    // Ensure canonical URL is clean and consistent
    // Always use the clean URL without query params or hash
    const canonicalUrl = seoData.canonical || currentUrl;
    // Remove any query params or hash that might have been included
    const finalCanonicalUrl = canonicalUrl.split('?')[0].split('#')[0];
    canonical.setAttribute("href", finalCanonicalUrl);
    
    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    
    updateOGTag("og:title", seoData.ogTitle || seoData.title);
    updateOGTag("og:description", seoData.ogDescription || seoData.description);
    updateOGTag("og:url", finalCanonicalUrl);
    updateOGTag("og:image", seoData.ogImage);
    updateOGTag("og:type", seoData.type || "website");
    updateOGTag(
      "og:locale",
      seoData.locale || language.replace(/-/g, "_"),
    );
    
    if (seoData.alternateLocale) {
      updateOGTag("og:locale:alternate", seoData.alternateLocale);
    }
    
    // Update published/modified time for articles
    if (seoData.publishedTime) {
      updateOGTag("article:published_time", seoData.publishedTime);
    }
    if (seoData.modifiedTime) {
      updateOGTag("article:modified_time", seoData.modifiedTime);
    }
    if (seoData.author) {
      updateOGTag("article:author", seoData.author);
    }
    
    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    
    updateTwitterTag("twitter:card", "summary_large_image");
    updateTwitterTag("twitter:site", `@${BRAND_HANDLE}`);
    updateTwitterTag("twitter:creator", `@${BRAND_HANDLE}`);
    updateTwitterTag("twitter:title", seoData.ogTitle || seoData.title);
    updateTwitterTag("twitter:description", seoData.ogDescription || seoData.description);
    updateTwitterTag("twitter:image", seoData.ogImage);
    
    // ── Hreflang ────────────────────────────────────────────────────────────
    // Emite link rel=alternate por cada idioma soportado. Estrategia:
    //   1. Bilingual paths con URL distinta per language (e.g. /creadora-de-
    //      contenido ↔ /content-creator) → declarar paths separados.
    //   2. Resto de rutas indexables → mismo path con `?lang=<code>` para
    //      idiomas alternos. La home (/) y /ser-miembro usan este patrón.
    //   3. Rutas noindex → solo x-default (no confunde a crawlers).
    //
    // Sin esto, Ahrefs/Google solo veían `x-default` y trataban es/en/pt/fr/de
    // como la misma página, perdiendo discoverability per-language.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const addHreflang = (lang: string, url: string) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", url);
      document.head.appendChild(link);
    };
    const SUPPORTED_LANGS = ["es", "en", "pt", "fr", "de"] as const;
    if (cleanPath === "/creadora-de-contenido" || cleanPath === "/content-creator") {
      // Bilingual pair: paths separados, sin ?lang.
      const esPath = `${baseUrl}/creadora-de-contenido`;
      const enPath = `${baseUrl}/content-creator`;
      addHreflang("es", esPath);
      addHreflang("en", enPath);
      addHreflang("x-default", esPath);
    } else if (cleanPath === "/en") {
      // English home: par bilingüe con la home ES (/). El inglés vive en su
      // propio path indexable con cluster del norte (en-US/GB/CA); x-default
      // queda en la home ES (mercado primario AR/LATAM).
      const esHome = `${baseUrl}/`;
      const enHome = `${baseUrl}/en`;
      addHreflang("es", esHome);
      addHreflang("en", enHome);
      addHreflang("en-US", enHome);
      addHreflang("en-GB", enHome);
      addHreflang("en-CA", enHome);
      addHreflang("x-default", esHome);
    } else if (shouldNoIndex) {
      // noindex paths: solo x-default — no propagamos a otros idiomas.
      addHreflang("x-default", finalCanonicalUrl);
    } else if (
      typeof pageKeyOrConfig === "object" &&
      pageKeyOrConfig !== null &&
      typeof seoData.locale === "string" &&
      seoData.canonical
    ) {
      // Country landings y páginas con locale fijo: paridad con static HTML +
      // sitemap (sin ?lang= en URLs indexables US/LATAM).
      const localeTag = seoData.locale.replace("_", "-");
      const pageUrl = finalCanonicalUrl;
      if (localeTag.startsWith("en")) {
        addHreflang(localeTag, pageUrl);
        addHreflang("en", pageUrl);
        addHreflang("en-US", pageUrl);
        addHreflang("en-GB", pageUrl);
        addHreflang("en-CA", pageUrl);
        addHreflang("en-AU", pageUrl);
        addHreflang("x-default", pageUrl);
      } else if (localeTag.startsWith("es")) {
        addHreflang(localeTag, pageUrl);
        addHreflang("es", pageUrl);
        addHreflang("es-AR", pageUrl);
        addHreflang("es-MX", pageUrl);
        addHreflang("es-CO", pageUrl);
        addHreflang("es-PE", pageUrl);
        addHreflang("es-CL", pageUrl);
        addHreflang("es-ES", pageUrl);
        addHreflang("x-default", pageUrl);
      } else {
        for (const lng of SUPPORTED_LANGS) {
          const href = lng === "es" ? finalCanonicalUrl : `${finalCanonicalUrl}?lang=${lng}`;
          addHreflang(lng, href);
        }
        addHreflang("x-default", finalCanonicalUrl);
      }
    } else {
      // Resto: emite los 5 idiomas con ?lang=, default = canonical sin query.
      for (const lng of SUPPORTED_LANGS) {
        const href = lng === "es" ? finalCanonicalUrl : `${finalCanonicalUrl}?lang=${lng}`;
        addHreflang(lng, href);
      }
      addHreflang("x-default", finalCanonicalUrl);
    }

    // ── Open Graph image: dimensions + alt ──────────────────────────────────
    // OG spec requiere width/height para preview correcto en social.
    // 1200x630 es el ratio óptimo de FB/Twitter (verificado en public/og.png).
    updateOGTag("og:image:width", "1200");
    updateOGTag("og:image:height", "630");
    updateOGTag("og:image:alt", `${BRAND_PERSON_NAME} — ${seoData.title}`);
    updateOGTag("og:image:type", "image/png");

    // ── Twitter image alt (accessibility + SEO) ─────────────────────────────
    updateTwitterTag("twitter:image:alt", `${BRAND_PERSON_NAME} — ${seoData.title}`);
    
    // ── Structured data dinámico (Person + WebSite + SearchAction) ──────────
    // Antes solo emitía Person para region=US; ahora se emite para TODAS las
    // regiones porque la Person entity es independiente del visitor (la
    // creator es la misma en AR/intl). WebSite con potentialAction permite
    // a Google generar SiteLinks de búsqueda dentro del SERP, mejorando CTR.
    let structuredData = document.querySelector("#structured-data-biolink");
    if (!structuredData) {
      structuredData = document.createElement("script");
      structuredData.setAttribute("type", "application/ld+json");
      structuredData.setAttribute("id", "structured-data-biolink");
      document.head.appendChild(structuredData);
    }
    const ogImageAbsolute = seoData.ogImage?.startsWith("http")
      ? seoData.ogImage
      : `${baseUrl}${seoData.ogImage || "/og.png"}`;
    const bioLinkSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Person",
          "@id": `${baseUrl}/#person`,
          name: BRAND_PERSON_NAME,
          alternateName: BRAND_HANDLE,
          description: seoData.description,
          url: baseUrl,
          image: ogImageAbsolute,
          sameAs: [
            `https://www.instagram.com/${BRAND_HANDLE}/`,
            `https://www.tiktok.com/@${BRAND_HANDLE}`,
            `https://www.youtube.com/@${BRAND_HANDLE}`,
          ],
          jobTitle: "Content Creator",
          knowsAbout: ["Lifestyle", "Content Creation", "Photography", "Social Media", "Fashion"],
        },
        {
          "@type": "WebSite",
          "@id": `${baseUrl}/#website`,
          url: baseUrl,
          name: BRAND_PERSON_NAME,
          description: seoData.description,
          inLanguage: language,
          publisher: { "@id": `${baseUrl}/#person` },
          // SearchAction: habilita SiteLinks Search Box en Google SERP
          // (caja de búsqueda interna del site). Aunque no hay endpoint
          // /search real, declararlo le da a Google la señal de que el site
          // tiene profundidad de contenido browseable.
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: `${baseUrl}/?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "WebPage",
          "@id": `${finalCanonicalUrl}#webpage`,
          url: finalCanonicalUrl,
          name: seoData.title,
          description: seoData.description,
          inLanguage: language,
          isPartOf: { "@id": `${baseUrl}/#website` },
          about: { "@id": `${baseUrl}/#person` },
          primaryImageOfPage: { "@type": "ImageObject", url: ogImageAbsolute },
        },
      ],
    };
    structuredData.textContent = JSON.stringify(bioLinkSchema);

  }, [location.pathname, language, pageKeyOrConfig, region]);
};
